import React, { useState, useEffect } from "react";
import BodyHeader from "../common/BodyHeader";
import InputCell from "assets/Home/InputCell.svg";
import SearchIcon from "assets/icons/Search.svg";
import ChatImage1 from "assets/images/ChatImg1.png";
import ChatImage2 from "assets/images/ChatImg2.png";
import ChatImage3 from "assets/images/ChatImg3.png";
import ChatImage4 from "assets/images/ChatImg4.png";
import ChatImage5 from "assets/images/ChatImg5.png";
import ChatImage6 from "assets/images/ChatImg6.png";
import ChatImage7 from "assets/images/ChatImg7.png";
import FileIcon from "assets/icons/File.svg";
import { Divider } from "@mui/material";
import FaceNeutral from "assets/icons/FaceNeutral.svg";
import Send from "assets/icons/Send.svg";
import Attachment from "assets/icons/Attachment.svg";
import Messages from "../common/Messages/Messages";
import { Messages as MessagesApi } from "../../../service.js";
import useApiHandler from "../../hooks/ApiHandler";

const MessagesScreen = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { callApi } = useApiHandler();

  // Fetch messages from API
  const fetchMessages = async (search = "", opts = {}) => {
    const { silent } = opts;
    try {
      if (!silent) setLoading(true);
      const response = await callApi(
        () => MessagesApi.getMessagesList(1, 50, search),
        {
          silent,
          onSuccess: (data) => {
            const threads = data.data?.results || [];

            // Map the API response to the expected format
            const messagesWithDetails = threads.map((thread) => {
              return {
                id: thread.id,
                threadId: thread.id,
                name: thread.last_message?.sender_name || "Unknown",
                service: `Request #${thread.request}`,
                lastMessage: thread.last_message?.body || "No messages yet",
                timestamp: thread.last_message_at || thread.created_at,
                isOnline: true,
                hasOffer: false,
                hasJob: false,
                rating: 5,
                profileImage: "/path/to/default/avatar.png",
                unreadCount: thread.unread_count || 0,
                isRead: thread.unread_count === 0,
                // Additional properties from API
                request: thread.request,
                participants: thread.participants,
                created_at: thread.created_at,
                last_message_at: thread.last_message_at,
                last_message: thread.last_message,
                // Thread-level metadata used by common/Messages for change proposal logic
                topic: thread.topic,
                change_proposal: thread.change_proposal,
                // Include reschedule request metadata for RESCHEDULE_REQUEST threads
                reschedule_request: thread.reschedule_request,
                issuetype: thread.issuetype,
              };
            });

            setMessages(messagesWithDetails);
          },
          onError: (error) => {
            console.error("Failed to fetch messages:", error);
            // Fallback to mock data if API fails
            if (!silent) setMessages(mockMessages);
          },
        }
      );
    } catch (error) {
      console.error("Error fetching messages:", error);
      // Fallback to mock data if API fails
      if (!silent) setMessages(mockMessages);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Load messages on component mount
  useEffect(() => {
    fetchMessages();

    // 10s polling: keep the messages "alive" by re-fetching.
    // Uses silent/background mode so the page doesn't flicker or show a loader.
    const intervalId = setInterval(() => {
      // Pass the current search query to polling if it exists
      fetchMessages(searchQuery, { silent: true });
    }, 10000);

    return () => clearInterval(intervalId);
  }, [searchQuery]);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    fetchMessages(query);
  };

  // Mock data as fallback
  const mockMessages = [
    {
      id: 0,
      name: "James Plumbing Co.",
      service: "HVAC Repair",
      lastMessage: "Received an offer",
      timestamp: "1h ago",
      isOnline: true,
      hasOffer: true,
      rating: 4,
      profileImage: ChatImage1,
      proposal: {
        title: "Proposal for: HVAC Repair",
        date: "Jun 15, 2025",
        estimatedCost: "1 Day",
        timeframe: "Available this Friday (Between 6 AM - 12 AM)",
        message: `Hi there,

I've handled similar HVAC issues in the area. I can complete this within a day.

Let me know if Friday morning works for you.
Regards James Plumbing Co.`,
        attachments: ["License.pdf", "Insurance - certificate.png"],
      },
    },
    {
      id: 1,
      name: "AquaFix Services",
      service: "Faucet Replacement",
      lastMessage: "You: Can you send a photo?",
      timestamp: "1h ago",
      isOnline: true,
      hasOffer: false,
      rating: 5,
      profileImage: ChatImage2,
      chatMessages: [
        {
          sender: "them",
          time: "8:24 PM",
          message: "Hi! Yes - I'm available between 9:00 AM and 11:30 AM.",
        },
        {
          sender: "them",
          time: "8:24 PM",
          message: "Will someone be on-site to let us in?",
        },
        {
          sender: "them",
          time: "8:24 PM",
          message:
            "Just the HVAC unit and utility closet. Also - any parking restrictions we should know about?",
        },
        {
          sender: "you",
          time: "8:22 PM",
          message: "Hi James, thanks for the quote.",
        },
        {
          sender: "you",
          time: "8:25 PM",
          message: "Yes, I'll be there. What do you need access to?",
        },
      ],
    },
    {
      id: 2,
      name: "James Bobby",
      service: "HVAC Repair",
      lastMessage: "You: Can you send a photo?",
      timestamp: "2h ago",
      isOnline: true,
      hasOffer: false,
      rating: 3,
      profileImage: ChatImage3,
      chatMessages: [
        {
          sender: "you",
          time: "7:30 PM",
          message: "Can you send a photo of the electrical panel?",
        },
        {
          sender: "them",
          time: "7:45 PM",
          message: "Sure, I'll send it over in a few minutes.",
        },
      ],
    },
    {
      id: 3,
      name: "James Plumbing Co.",
      service: "HVAC Repair",
      lastMessage: "You: Can you send a photo?",
      timestamp: "2h ago",
      isOnline: true,
      hasOffer: false,
      rating: 3,
      profileImage: ChatImage4,
      chatMessages: [
        {
          sender: "you",
          time: "7:30 PM",
          message: "Can you send a photo of the electrical panel?",
        },
        {
          sender: "them",
          time: "7:45 PM",
          message: "Sure, I'll send it over in a few minutes.",
        },
      ],
    },
    {
      id: 4,
      name: "AquaFix Services",
      service: "HVAC Repair",
      lastMessage: "You: Can you send a photo?",
      timestamp: "2h ago",
      isOnline: true,
      hasOffer: false,
      rating: 3,
      profileImage: ChatImage5,
      chatMessages: [
        {
          sender: "you",
          time: "7:30 PM",
          message: "Can you send a photo of the electrical panel?",
        },
        {
          sender: "them",
          time: "7:45 PM",
          message: "Sure, I'll send it over in a few minutes.",
        },
      ],
    },
    {
      id: 5,
      name: "James Bobby",
      service: "HVAC Repair",
      lastMessage: "You: Can you send a photo?",
      timestamp: "2h ago",
      isOnline: true,
      hasOffer: false,
      rating: 3,
      profileImage: ChatImage6,
      chatMessages: [
        {
          sender: "you",
          time: "7:30 PM",
          message: "Can you send a photo of the electrical panel?",
        },
        {
          sender: "them",
          time: "7:45 PM",
          message: "Sure, I'll send it over in a few minutes.",
        },
      ],
    },
    {
      id: 6,
      name: "James Plumbing Co.",
      service: "HVAC Repair",
      lastMessage: "You: Can you send a photo?",
      timestamp: "2h ago",
      isOnline: true,
      hasOffer: false,
      rating: 3,
      profileImage: ChatImage7,
      chatMessages: [
        {
          sender: "you",
          time: "7:30 PM",
          message: "Can you send a photo of the electrical panel?",
        },
        {
          sender: "them",
          time: "7:45 PM",
          message: "Sure, I'll send it over in a few minutes.",
        },
      ],
    },
  ];
  return (
    <div className="max-w-[1440px] m-auto  md:px-6 px-4">
      <div className="pt-[83px]">
        <Messages
          title="All Messages"
          header={true}
          messages={messages}
          loading={loading}
          onSearch={handleSearch}
          searchQuery={searchQuery}
        />
      </div>
    </div>
  );
};

export default MessagesScreen;
